import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Seasons from './pages/Seasons';
import SeasonDetail from './pages/SeasonDetail';
import Predictions from './pages/Predictions';
import MyPredictions from './pages/MyPredictions';
import Leagues from './pages/Leagues';
import LeagueDetail from './pages/LeagueDetail';
import Leaderboards from './pages/Leaderboards';
import News from './pages/News';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCompetitions from './pages/admin/AdminCompetitions';
import AdminSeasons from './pages/admin/AdminSeasons';
import AdminTeams from './pages/admin/AdminTeams';
import AdminMatches from './pages/admin/AdminMatches';
import AdminMatchGroups from './pages/admin/AdminMatchGroups';
import AdminUsers from './pages/admin/AdminUsers';
import AdminMatchDistribution from './pages/admin/AdminMatchDistribution';
import AdminNews from './pages/admin/AdminNews';
import AdminNewsForm from './pages/admin/AdminNewsForm';
import AdminFavouriteTeams from './pages/admin/AdminFavouriteTeams';
import AdminTrophies from './pages/admin/AdminTrophies';
import AdminTrophyAwards from './pages/admin/AdminTrophyAwards';
import AdminNewsletter from './pages/admin/AdminNewsletter';
import AdminChatModeration from './pages/admin/AdminChatModeration';
import TrophyRoom from './pages/TrophyRoom';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Rules from './pages/Rules';
import FanLeague from './pages/FanLeague';
import SelectFavouriteTeam from './pages/SelectFavouriteTeam';
import NewUser from './pages/NewUser';
import KitDesigner from './pages/KitDesigner';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex flex-col">
            <Navbar />
            <main className="container mx-auto px-2 sm:px-4 py-8 flex-grow">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route 
                  path="/select-favourite-team" 
                  element={
                    <PrivateRoute>
                      <SelectFavouriteTeam />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/new-user" 
                  element={
                    <PrivateRoute>
                      <NewUser />
                    </PrivateRoute>
                  } 
                />
                <Route path="/seasons" element={<Seasons />} />
                <Route path="/seasons/:id" element={<SeasonDetail />} />
                <Route 
                  path="/predictions/:matchId" 
                  element={
                    <PrivateRoute>
                      <Predictions />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/my-predictions" 
                  element={
                    <PrivateRoute>
                      <MyPredictions />
                    </PrivateRoute>
                  } 
                />
                <Route path="/leagues" element={<Leagues />} />
                <Route path="/leagues/:id" element={<LeagueDetail />} />
                <Route 
                  path="/leaderboards" 
                  element={
                    <PrivateRoute>
                      <Leaderboards />
                    </PrivateRoute>
                  } 
                />
                <Route path="/news" element={<News />} />
                <Route path="/news/:slug" element={<News />} />
                <Route 
                  path="/profile" 
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  } 
                />
                <Route path="/users/:userId" element={<UserProfile />} />
                <Route path="/users/:userId/trophies" element={<TrophyRoom />} />
                <Route 
                  path="/trophies" 
                  element={
                    <PrivateRoute>
                      <TrophyRoom />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/kit-designer" 
                  element={
                    <PrivateRoute>
                      <KitDesigner />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/competitions" 
                  element={
                    <AdminRoute>
                      <AdminCompetitions />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/seasons" 
                  element={
                    <AdminRoute>
                      <AdminSeasons />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/teams" 
                  element={
                    <AdminRoute>
                      <AdminTeams />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/matches" 
                  element={
                    <AdminRoute>
                      <AdminMatches />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/matchgroups" 
                  element={
                    <AdminRoute>
                      <AdminMatchGroups />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/users" 
                  element={
                    <AdminRoute>
                      <AdminUsers />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/match-distribution" 
                  element={
                    <AdminRoute>
                      <AdminMatchDistribution />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/favourite-teams" 
                  element={
                    <AdminRoute>
                      <AdminFavouriteTeams />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/news" 
                  element={
                    <AdminRoute>
                      <AdminNews />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/news/create" 
                  element={
                    <AdminRoute>
                      <AdminNewsForm />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/news/edit/:id" 
                  element={
                    <AdminRoute>
                      <AdminNewsForm />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/trophies" 
                  element={
                    <AdminRoute>
                      <AdminTrophies />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/trophies/:trophyId/awards" 
                  element={
                    <AdminRoute>
                      <AdminTrophyAwards />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/trophy-awards" 
                  element={
                    <AdminRoute>
                      <AdminTrophyAwards />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/newsletter" 
                  element={
                    <AdminRoute>
                      <AdminNewsletter />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/admin/chat-moderation" 
                  element={
                    <AdminRoute>
                      <AdminChatModeration />
                    </AdminRoute>
                  } 
                />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/rules" element={<Rules />} />
                {/* Fan League dynamic route - must be last to avoid conflicts */}
                <Route path="/:groupname" element={<FanLeague />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App; 